import {
  Show,
  SimpleShowLayout,
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  TextField,
  ReferenceArrayField,
  ReferenceField,
  TopToolbar,
  Labeled,
  SingleFieldList,
  ChipField,
  useShowController,
} from "react-admin";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";

import BackButtonToolbar from "features/BackButtonToolbar";
import { SectionTitle } from "features/SectionTitle";
import { settings } from "app";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { getAvailability } from "../helpers";
import ApproveJob from "components/CareerCenter/Jobs/ApproveJob";
import RejectJob from "components/CareerCenter/Jobs/RejectJob";
import ReturnJobForCorrection from "components/CareerCenter/Jobs/ReturnJobForCorrection";
import { careerCenterRequests } from "api/careerCenterApi";
import { administrationRequests } from "api/administrationApi";

const ShowJob = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const { record } = useShowController();

  const { isAvailableForCorrection, isAvailableForApprove, isAvailableForReject } = getAvailability(record?.status);

  return (
    <Show
      title="Детайли на обява за работа"
      actions={
        <TopToolbar sx={{ paddingTop: "2rem", paddingRight: "2rem", gap: "1rem" }}>
          {isAvailableForApprove && (
            <SecuredComponent permission={permissions.canManageJobs}>
              <ApproveJob />
            </SecuredComponent>
          )}
          {isAvailableForReject && (
            <SecuredComponent permission={permissions.canManageJobs}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems={"center"}
                pb={1}
              >
                <RejectJob />
              </Box>
            </SecuredComponent>
          )}
          {isAvailableForCorrection ? (
            <SecuredComponent permission={permissions.canManageJobs}>
              <ReturnJobForCorrection />
            </SecuredComponent>
          ) : null}
          <BackButtonToolbar />
        </TopToolbar>
      }
    >
      <SimpleShowLayout>
        <SectionTitle
          paddingY={2}
          title="Информация за обявата"
          textAlign="center"
          fontSize={14}
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип обява">
              <ReferenceField
                source="type"
                reference={careerCenterRequests.getJobOfferTypesSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Категория">
              <ReferenceField
                source="category"
                reference={careerCenterRequests.getJobCategoryTypesSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Длъжност">
              <TextField source="position" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Описание на длъжността">
              <TextField source="positionDescription" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Изисквания към кандидатите">
              <TextField source="requirements" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип кандидат">
              <ReferenceArrayField
                source="candidateTypes"
                reference={careerCenterRequests.getJobOffersCandidateTypesSelect()}
              >
                <SingleFieldList linkType={false}>
                  <ChipField
                    source="nameBg"
                    linkType={false}
                  />
                </SingleFieldList>
              </ReferenceArrayField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Фирмата предлага">
              <TextField source="companyOffers" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип заетост">
              <ReferenceField
                source="employmentType"
                reference={careerCenterRequests.getEmploymentTypesSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип договор">
              <ReferenceField
                source="contractType"
                reference={careerCenterRequests.getContractTypesSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Заплата">
              <TextField source="salary" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Необходими документи за кандидатстване">
              <TextField source="applicationDocuments" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Начин на кандидатстване">
              <ReferenceField
                source="applicationMethods"
                reference={careerCenterRequests.getJobApplicationMethodsSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Публикувана до">
              <DateField
                source="validUntil"
                locales="bg-BG"
                showTime
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Месторабота (населено място)">
              <ReferenceField
                source="settlementId"
                reference={administrationRequests.getSettlementsSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Квартал">
              <TextField source="district" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Адрес">
              <TextField source="address" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Лице за контакт">
              <TextField source="contactPerson" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Имейл за контакт">
              <TextField source="contactPersonEmail" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Телефон за контакт">
              <TextField source="contactPersonPhone" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Статус">
              <ReferenceField
                label="Статус"
                source="status"
                reference={careerCenterRequests.getStatusesSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
        </Grid>

        <SectionTitle
          paddingY={2}
          title="Кандидатствали"
          textAlign="center"
          fontSize={14}
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            {record?.jobApplications?.length > 0 ? (
              <ArrayField source="jobApplications">
                <Datagrid
                  bulkActionButtons={false}
                  rowClick={false}
                  size="small"
                  sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
                >
                  <FunctionField
                    label="Имена"
                    sortable={false}
                    render={applicant =>
                      [applicant.firstName, applicant.secondName, applicant.lastName].filter(Boolean).join(" ") || "-"
                    }
                  />
                  <TextField
                    source="email"
                    label="Имейл"
                    sortable={false}
                    emptyText="-"
                  />
                  <DateField
                    source="createdOn"
                    label="Дата на кандидатстване"
                    locales="bg-BG"
                    showTime
                    sortable={false}
                    emptyText="-"
                  />
                </Datagrid>
              </ArrayField>
            ) : (
              <Typography>Няма кандидатствали.</Typography>
            )}
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};
export default ShowJob;
